import React, { useEffect, useMemo, useState } from 'react';
import { ExpandMoreRounded as ExpandMoreRoundedIcon } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Collapse,
  Paper,
  type SxProps,
  Table,
  TableContainer,
  type Theme,
  Typography,
} from '@mui/material';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';
import { taskTableStyles } from '../taskUi.tsx';
import { TASKS_ROWS_PER_PAGE } from '../constants.ts';
import { taskServicesConfig } from '../taskConfig.ts';
import { tasksPageStyles } from '../styles.ts';
import { getTaskSectionMetaText, hasImmediateTasks } from '../utils.ts';
import TasksServiceTableBody from './TasksServiceTableBody.tsx';
import TasksServiceTableHead from './TasksServiceTableHead.tsx';
import type { ITasksServiceTableProps } from './types.ts';

const TasksServiceTable: React.FC<ITasksServiceTableProps> = ({
  activeGuaranteesActionId,
  canAssignGuaranteesTask,
  emptyText,
  guaranteesDateSortDirection,
  isLoading = false,
  onGuaranteesDateSortToggle,
  onGuaranteesAssignTask,
  onGuaranteesRefuseTask,
  onGuaranteesSlaDeadlineReached,
  onGuaranteesTakeInWork,
  onPageChange,
  page,
  rows,
  rowsPerPage = TASKS_ROWS_PER_PAGE,
  serviceKey,
  totalElements,
}) => {
  const serviceConfig = taskServicesConfig[serviceKey];
  const [isExpanded, setIsExpanded] = useState(!!rows.length || !!emptyText);
  const [localPage, setLocalPage] = useState(0);
  const isServerPaginated = page !== undefined && totalElements !== undefined && !!onPageChange;
  const activePage = isServerPaginated ? page : localPage;
  const activeTotalElements = totalElements ?? rows.length;
  const effectiveEmptyText = emptyText ?? serviceConfig.emptyText;
  const hasTasksToDoNow = hasImmediateTasks(rows);
  const lastPage = Math.max(Math.ceil(activeTotalElements / rowsPerPage) - 1, 0);
  const currentPage = Math.min(activePage, lastPage);
  const paginatedRows = useMemo(() => {
    if (isServerPaginated) {
      return rows;
    }

    const startIndex = currentPage * rowsPerPage;

    return rows.slice(startIndex, startIndex + rowsPerPage);
  }, [currentPage, isServerPaginated, rows, rowsPerPage]);

  useEffect(() => {
    if (rows.length || emptyText) {
      setIsExpanded(true);
    }
  }, [emptyText, rows.length]);

  useEffect(() => {
    if (!isServerPaginated && localPage > lastPage) {
      setLocalPage(lastPage);
    }
  }, [isServerPaginated, lastPage, localPage]);

  const handlePageChange = (nextPage: number) => {
    if (isServerPaginated) {
      onPageChange?.(nextPage);
      return;
    }

    setLocalPage(nextPage);
  };

  return (
    <Paper
      elevation={0}
      sx={[taskTableStyles.documentTableSection, tasksPageStyles.sectionPaper] as SxProps<Theme>}
    >
      <ButtonBase
        onClick={() => setIsExpanded((previousValue) => !previousValue)}
        sx={tasksPageStyles.sectionToggle}
      >
        <Box sx={tasksPageStyles.sectionToggleContent}>
          <Typography component="span" sx={tasksPageStyles.sectionTitle}>
            {serviceConfig.title}
          </Typography>
          <Box component="span" sx={tasksPageStyles.sectionMetaWrap}>
            {hasTasksToDoNow && <Box component="span" sx={tasksPageStyles.sectionUrgencyDot} />}
            <Typography component="span" sx={tasksPageStyles.sectionMeta}>
              {getTaskSectionMetaText(activeTotalElements, effectiveEmptyText)}
            </Typography>
          </Box>
        </Box>

        <ExpandMoreRoundedIcon sx={tasksPageStyles.sectionToggleIcon(isExpanded)} />
      </ButtonBase>

      <Collapse in={isExpanded} timeout="auto">
        <Box sx={tasksPageStyles.sectionContent}>
          <TableContainer sx={tasksPageStyles.tableContainer}>
            <Table sx={tasksPageStyles.table(serviceKey)}>
              <TasksServiceTableHead
                serviceKey={serviceKey}
                guaranteesDateSortDirection={guaranteesDateSortDirection}
                onGuaranteesDateSortToggle={onGuaranteesDateSortToggle}
              />
              <TasksServiceTableBody
                activeGuaranteesActionId={activeGuaranteesActionId}
                canAssignGuaranteesTask={canAssignGuaranteesTask}
                emptyText={emptyText}
                onGuaranteesAssignTask={onGuaranteesAssignTask}
                onGuaranteesRefuseTask={onGuaranteesRefuseTask}
                onGuaranteesSlaDeadlineReached={onGuaranteesSlaDeadlineReached}
                onGuaranteesTakeInWork={onGuaranteesTakeInWork}
                rows={paginatedRows}
                serviceKey={serviceKey}
              />
            </Table>
          </TableContainer>

          <Box sx={tasksPageStyles.tablePagination}>
            <TablePaginationControls
              page={currentPage}
              rowsPerPage={rowsPerPage}
              totalElements={activeTotalElements}
              isLoading={isLoading}
              onPageChange={handlePageChange}
              onRowsPerPageChange={() => handlePageChange(0)}
            />
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TasksServiceTable;
